import { Box } from '@dtx-company/design-system/src/components/Box/Box'
import { PrismicRichText } from '@prismicio/react'
import { RichTextField, asText } from '@prismicio/client'
import { Typography } from '@dtx-company/design-system/src'
import { styled } from '@mui/system'
import { usePrismicAccordionState } from './hooks/usePrismicAccordionState'
import AddIcon from '@mui/icons-material/Add'
import MuiAccordion, { AccordionProps as MuiAccordionProps } from '@mui/material/Accordion'
import MuiAccordionDetails from '@mui/material/AccordionDetails'
import MuiAccordionSummary, { AccordionSummaryProps } from '@mui/material/AccordionSummary'
import React from 'react'
import RemoveIcon from '@mui/icons-material/Remove'

const AccordionStyled = styled((props: MuiAccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  maxWidth: '100%',
  backgroundColor: 'transparent',
  borderTop: `1px solid ${theme.builder.primary00}`
}))

const AccordionDetails = styled(MuiAccordionDetails)(() => ({
  overflowY: 'scroll',
  padding: 0
}))

const PrismicRichTextWrapper = styled(Box)`
  font-size: 1.125rem;

  a {
    text-decoration: underline;
    font-weight: inherit;
  }

  // Remove top margin from 1st element because element on top (AccordionSummary button) already provides space
  *:first-of-type {
    margin-top: 0;
  }
`

const AccordionSummary = styled(
  ({ expanded, children, ...rest }: AccordionSummaryProps & { expanded: boolean }) => (
    <MuiAccordionSummary {...rest}>
      {children}
      {expanded ? <RemoveIcon /> : <AddIcon />}
    </MuiAccordionSummary>
  )
)(({ theme }) => ({
  color: theme.builder.primary00,
  paddingTop: theme.spacing(5),
  paddingRight: 0,
  paddingBottom: theme.spacing(5),
  paddingLeft: 0,

  '& .MuiAccordionSummary-content': {
    justifyContent: 'space-between',
    alignItems: 'center',
    margin: 'unset'
  },
  '& .MuiSvgIcon-root': {
    marginLeft: theme.spacing(4),
    fontSize: '1.2rem'
  }
}))

export interface PrismicAccordionItem {
  title: RichTextField
  description: RichTextField
}

export interface PrismicAccordionProps {
  items: PrismicAccordionItem[]
  /** Callback usually for heap tracking of the item of the accordion that was toggle expanded/collapsed */
  onToggledAccordion?: (title: string, expanded: boolean) => void
  /** Whether or not all items in the accordion should start expanded by default */
  shouldItemsStartExpanded?: boolean
}

/**
 * Accordion component to render content that comes from Prismic from the Accordion Slice
 */
export const PrismicAccordion = ({
  items,
  onToggledAccordion,
  shouldItemsStartExpanded = false
}: PrismicAccordionProps): JSX.Element => {
  const { expandedItemsByTitle, toggleExpandItem } = usePrismicAccordionState({
    shouldItemsStartExpanded,
    items
  })

  return (
    <Box>
      {items.map((item, i) => {
        const titleAsText = asText(item.title)
        const descAsText = asText(item.description)

        if (!titleAsText || !descAsText) return null
        return (
          <AccordionStyled
            expanded={expandedItemsByTitle[titleAsText]}
            onChange={_event => {
              toggleExpandItem(titleAsText)
              onToggledAccordion?.(titleAsText, !expandedItemsByTitle[titleAsText])
            }}
            key={i}
          >
            <AccordionSummary
              data-heap-id={`PrismicAccordion${i}Button`}
              expanded={expandedItemsByTitle[titleAsText]}
            >
              <Typography component="div" variant="subtitleMedium">
                {titleAsText}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <PrismicRichTextWrapper>
                <PrismicRichText field={item.description} />
              </PrismicRichTextWrapper>
            </AccordionDetails>
          </AccordionStyled>
        )
      })}
    </Box>
  )
}
